import React, { useState, useEffect } from 'react';
import {
  X,
  Check,
  User,
  Utensils,
  UtensilsCrossed,
  FileSpreadsheet,
} from 'lucide-react';

import ScanBadgeButton from '../../../components/scan-badge-button/scan-badge-button';

import { fetchUserRecord } from '../../../utils/mongoDataOperations';
import { getMeal, getMealCode, updateUsedMeals } from '../../../utils/helpers';

const StatusBar = ({ type, title, message, dietaryRestrictions }) => {
  const getStatusStyles = () => {
    switch (type) {
      case 'error':
        return 'bg-red-500 text-white';
      case 'success':
        return 'bg-green-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <div className={`w-full p-4 ${getStatusStyles()}`}>
      <div className='flex items-center justify-between max-w-4xl mx-auto'>
        <div className='flex items-center space-x-4'>
          {type === 'error' ? (
            <X className='h-6 w-6' />
          ) : (
            <Check className='h-6 w-6' />
          )}
          <div>
            <h2 className='font-bold text-xl'>{title}</h2>
            <p className='opacity-90'>{message}</p>
          </div>
        </div>
        {dietaryRestrictions && (
          <div className='bg-white px-4 py-2 rounded-lg text-green-700'>
            <p className='font-medium'>
              Dietary Restrictions: {dietaryRestrictions}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const Meals = () => {
  const [scannedReturnedID, setScannedReturnedID] = useState(null);
  const [scannedUserRecord, setScannedUserRecord] = useState(null);
  const [alreadyUsed, setAlreadyUsed] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (scannedReturnedID) {
        try {
          const response = await fetchUserRecord(scannedReturnedID);
          setScannedUserRecord(response);
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };
    fetchData();
  }, [scannedReturnedID]);

  useEffect(() => {
    if (scannedUserRecord) {
      const mealCode = getMealCode();
      const mealInfo = scannedUserRecord?.meal_info;

      if (mealInfo?.usedMeals?.includes(mealCode)) {
        setAlreadyUsed(true);
      } else {
        setAlreadyUsed(false);
        updateUsedMeals(scannedReturnedID, scannedUserRecord, mealCode);
      }
    }
  }, [scannedUserRecord, scannedReturnedID]);

  // Reset alreadyUsed when scannedReturnedID changes
  useEffect(() => {
    setAlreadyUsed(null);
  }, [scannedReturnedID]);

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className='flex items-center mb-4 space-x-3'>
      <div className='bg-gray-100 flex h-8 items-center justify-center rounded-full w-8'>
        <Icon className='h-4 text-gray-600 w-4' />
      </div>
      <div>
        <p className='text-gray-500 text-sm'>{label}</p>
        <p className='font-medium'>{value || '...'}</p>
      </div>
    </div>
  );

  const getStatusBar = () => {
    if (!scannedUserRecord) return null;

    if (!scannedUserRecord?.check_in_info?.checkedIn) {
      return (
        <StatusBar
          type='error'
          title='Check In Required'
          message='Hacker not checked in yet'
        />
      );
    }

    if (alreadyUsed === true) {
      return (
        <StatusBar
          type='error'
          title='Meal Already Used'
          message='This meal has already been claimed'
        />
      );
    }

    if (alreadyUsed === false) {
      return (
        <StatusBar
          type='success'
          title='Meal Approved'
          message='Hacker is clear to receive their meal'
          dietaryRestrictions={
            scannedUserRecord?.meal_info?.dietaryRestrictions
          }
        />
      );
    }

    return null;
  };

  return (
    <>
      {getStatusBar()}
      <div className='max-w-4xl mx-auto p-6 space-y-6'>
        {/* Scan Controls */}
        <div className='flex items-center justify-between'>
          <a
            href='https://docs.google.com/spreadsheets/d/1iHw-U04HeLeKiEuDx-HYT2YvNc3MPpik6LMvsxPdvHU/edit?usp=sharing'
            target='_blank'
            rel='noopener noreferrer'
            className='bg-yellow-400 hover:bg-yellow-500 inline-flex items-center px-4 py-2 rounded-lg text-gray-700 transition-colors'>
            <FileSpreadsheet className='h-5 mr-2 w-5' />
            Dietary Restrictions
          </a>
          <div className='flex space-x-3'>
            <button
              onClick={() => {
                setScannedUserRecord(null);
                setScannedReturnedID(null);
              }}
              className='border border-gray-300 hover:bg-gray-50 inline-flex items-center px-4 py-2 rounded-lg text-gray-700 transition-colors'>
              Clear
            </button>
            <ScanBadgeButton
              scannedReturnedID={scannedReturnedID}
              setScannedReturnedID={setScannedReturnedID}
            />
          </div>
        </div>

        {/* Header with Active Meal */}
        <div className='bg-white p-6 rounded-lg shadow text-center'>
          <div className='flex items-center justify-center mb-2'>
            <Utensils className='h-6 mr-2 text-tree-green w-6' />
            <h2 className='font-semibold text-2xl'>Active Meal</h2>
          </div>
          <p className='text-xl'>
            {getMeal() === null
              ? 'No meals currently active'
              : `${new Date().toLocaleString('en-us', {
                  weekday: 'long',
                })} ${getMeal()}`}
          </p>
        </div>

        {/* Hacker Information */}
        {scannedUserRecord?.application_info && (
          <div className='bg-white p-6 rounded-lg shadow'>
            <h2 className='font-semibold mb-4 text-xl'>Hacker Information</h2>
            <InfoItem
              icon={User}
              label='Full Name'
              value={scannedUserRecord?.application_info?.full_name}
            />
            <InfoItem
              icon={User}
              label='Gender'
              value={scannedUserRecord?.application_info?.gender}
            />
            <InfoItem
              icon={UtensilsCrossed}
              label='Dietary Restrictions'
              value={scannedUserRecord?.meal_info?.dietaryRestrictions || '...'}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Meals;
